import type { FormEvent } from 'react'
import type { Demand, Buyer } from 'types/buyer'
import type { SearchBody } from 'types'

import { useEffect, useState, useRef } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'

import { dimmBackground, formatDate, formatNIP, formatPhoneNumber } from 'utils'
import { useStore } from 'store'
import { STATUS, demandStatuses, URL_PARAM } from 'common'
import copy from 'copy.json'
import { adminPanelPaths } from 'paths'

import { ArrowLeftIcon, AssignmentIcon, PencilIcon, PowerIcon } from 'icons'
import Badge from 'components/Badge'
import Button from 'components/Button'
import Consents from 'components/Consents'
import ContentHeader from 'components/ContentHeader'
import DemandDetailsRow from 'pages/buyer/DemandDetailsRow'
import EmailConfirmation from 'components/EmailConfirmation'
import PageContainer from 'components/PageContainer'
import Scroll from 'components/Scroll'
import Spinner from 'components/Spinner'
import Status from 'components/Status'
import StatusesFilter from 'components/StatusesFilter'
import Table from 'components/Table'
import InputLabel from 'components/InputLabel'
import Input from 'components/Input'

import EditClientModal from 'modals/EditClientModal'
import ConfirmationActivateClientModal from 'modals/ConfirmationActivateClientModal'

const text_form = copy.common.form
const text = copy.buyer.details
const button = copy.common.button

export default function DetailsScreen() {
  const { buyerId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { api, searchDemands, demands, getUser, clearFieldErrors } = useStore()
  const timeout = useRef(0)
  const [searchParams] = useSearchParams()

  const defaultSearchValues: SearchBody = {
    page_number: 0,
    statuses: [],
    electric_energy_meter_number: searchParams.get(URL_PARAM.electricEnergyMeterNumber)
  }

  const [loading, setLoading] = useState(false)
  const [currentUserData, setCurrentUserData] = useState<Buyer | null>(null)
  const [filterStatuses, setFilterStatuses] = useState<Demand['status'][]>([])
  const [editClientModalOpen, setEditClientModalOpen] = useState(false)
  const [activateClientModalOpen, setActivateClientModalOpen] = useState(false)
  const [searchValues, setSearchValues] = useState<SearchBody>(defaultSearchValues)

  const isBackPageSaved = location?.state?.rememberBackPage

  const getBuyerData = async (buyerId: number) => {
    setCurrentUserData(await api.getAdminClient('buyer', buyerId))
    await getUser('buyer', Number(buyerId)) // Save current user in store
  }

  useEffect(() => {
    if (buyerId) {
      setLoading(true)

      getBuyerData(parseInt(buyerId))
        .then(() => setLoading(false))
        .catch(console.error)
    }
  }, []) // eslint-disable-line

  useEffect(() => {
    if (buyerId) {
      const getDemandsData = async () => {
        await searchDemands(parseInt(buyerId), searchValues)
      }

      getDemandsData()
    }
  }, [searchValues, buyerId]) // eslint-disable-line

  useEffect(dimmBackground)

  const handleChosenStatuses = (statuses: Demand['status'][]) => {
    setFilterStatuses(statuses)
    setSearchValues(state => ({ ...state, statuses }))
  }

  const handleBuyerActivation = async (buyerId: number) => {
    await api.activateClient('buyer', buyerId)
    getBuyerData(buyerId)
    setActivateClientModalOpen(false)
  }

  const handleAssignToAgent = async (sellerId: number) => {
    await api.assignToAgent(sellerId, 'buyer')
    getBuyerData(Number(sellerId))
  }

  const handleSearch = (event: FormEvent<HTMLInputElement>) => {
    clearFieldErrors(event.currentTarget.name)
    clearTimeout(timeout.current)

    const form = document.getElementById('search-demands-admin-panel') as HTMLFormElement
    const values = Array.from(new FormData(form)).reduce(
      (result, [name, value]) => ({ ...result, [name]: value !== '' ? value : null }),
      {} as SearchBody
    )

    timeout.current = window.setTimeout(
      () => setSearchValues(state => ({ ...state, ...values })),
      500
    )
  }

  if (!currentUserData) return null

  const isClientActivateAvailable =
    currentUserData.registration_source === 'WEB' && currentUserData.status === 'INACTIVE'

  const consentsArray = [
    { name: text_form.privacy_consent, agreed: currentUserData.privacy_consent_agree },
    { name: text_form.marketing_consent, agreed: currentUserData.marketing_consent_agree },
    {
      name: text_form.marketing_third_party_consent,
      agreed: currentUserData.marketing_third_party_consent_agree
    }
  ]

  return (
    <>
      <PageContainer>
        <ContentHeader>
          <BackArrowButton
            onClick={() =>
              isBackPageSaved ? navigate(-1) : navigate(adminPanelPaths.buyer.search)
            }
          >
            <ArrowLeftIcon />
          </BackArrowButton>

          <Badge user>{text.user}</Badge>

          <h1>{currentUserData.company_name}</h1>

          <ButtonWrapper>
            <Button secondary onClick={() => setEditClientModalOpen(true)}>
              <PencilIcon /> {button.edit}
            </Button>

            <Button secondary onClick={() => handleAssignToAgent(currentUserData.id)}>
              <AssignmentIcon /> {button.assign_to_me}
            </Button>

            {isClientActivateAvailable && (
              <Button onClick={() => setActivateClientModalOpen(true)}>
                <PowerIcon /> {button.activate}
              </Button>
            )}
          </ButtonWrapper>
        </ContentHeader>

        <Scroll>
          <TableWrapper>
            <Table>
              <tbody>
                <tr>
                  <td>{text_form.company_name}</td>
                  <td>
                    <strong>{currentUserData.company_name}</strong>
                  </td>
                </tr>
                <tr>
                  <td>{text_form.login}</td>
                  <td>{currentUserData.business_id}</td>
                </tr>
                <tr>
                  <td>{text_form.tax_identifier}</td>
                  <td>{formatNIP(currentUserData.tax_identifier)}</td>
                </tr>
                <tr>
                  <td>{text_form.phone_nr}</td>
                  <td>{formatPhoneNumber(currentUserData.phone_number)}</td>
                </tr>
                <tr>
                  <td>{text_form.email}</td>
                  <td>
                    <EmailConfirmation
                      emailConfirmed={currentUserData.email_confirmed}
                      contactEmail={currentUserData.contact_email}
                    />
                  </td>
                </tr>
              </tbody>
            </Table>

            <Table>
              <tbody>
                <tr>
                  <td>{text_form.status}</td>
                  <td>
                    <Status statusType={currentUserData.status} />
                  </td>
                </tr>
                <tr>
                  <td>{text_form.id}</td>
                  <td>
                    <strong>{currentUserData.id}</strong>
                  </td>
                </tr>
                <tr>
                  <td>{text_form.registration_source}</td>
                  <td>{currentUserData.registration_source}</td>
                </tr>
                <tr>
                  <td>{text_form.assigned_agent}</td>
                  <td>{currentUserData.assigned_agent || '-'}</td>
                </tr>
                <tr>
                  <td>{text_form.created_date}</td>
                  <td>{formatDate(currentUserData.created_date)}</td>
                </tr>
                <tr>
                  <td>{text_form.last_modified_date}</td>
                  <td>{formatDate(currentUserData.last_modified_date)}</td>
                </tr>
                <tr>
                  <td>{text_form.consents}</td>
                  <td>
                    <Consents consents={consentsArray} />
                  </td>
                </tr>
              </tbody>
            </Table>
          </TableWrapper>
        </Scroll>

        <form id="search-demands-admin-panel">
          <StatusesFilterStyled statuses={demandStatuses} onChosenStatus={handleChosenStatuses}>
            <InputLabel>
              <span>{text_form.electric_energy_meter_number}</span>
              <Input
                name="electric_energy_meter_number"
                defaultValue={searchValues.electric_energy_meter_number || ''}
                onInput={handleSearch}
              />
            </InputLabel>

            <Button
              secondary
              type="button"
              onClick={() =>
                navigate(adminPanelPaths.buyer.add_demand, { state: currentUserData.id })
              }
            >
              {text.add_new_resource}
            </Button>
          </StatusesFilterStyled>
        </form>

        <Scroll>
          <Table>
            <thead>
              <tr>
                <th>{text_form.status}</th>
                <th>{text_form.id}</th>
                <th>{text_form.distribution_name}</th>
                <th>{text_form.electric_energy_meter_number}</th>
                <th>{text_form.yearly_power_consumption}</th>
                <th>{text_form.current_energy_price}</th>
                <th />
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9}>
                    <Spinner style={{ margin: 'auto' }} />
                  </td>
                </tr>
              ) : (
                <>
                  {demands?.map(demand => (
                    <DemandDetailsRow demand={demand} key={demand.id} />
                  ))}

                  {!demands.length && (
                    <tr>
                      <td colSpan={9}>
                        {filterStatuses.length > 0
                          ? `${text.not_found} ${filterStatuses
                              .map(status => STATUS[status])
                              .join(', ')}`
                          : text.not_found_any}
                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </Table>
        </Scroll>
      </PageContainer>

      {/* Edit buyer data */}
      <EditClientModal
        userType="buyer"
        clientData={currentUserData}
        isModalOpen={editClientModalOpen}
        onModalClose={() => setEditClientModalOpen(false)}
        refreshData={() => getBuyerData(currentUserData.id)}
      />

      {/* Confirm buyer activation */}
      <ConfirmationActivateClientModal
        clientType="buyer"
        isModalOpen={activateClientModalOpen}
        onModalClose={() => setActivateClientModalOpen(false)}
        onModalConfirmation={() => handleBuyerActivation(currentUserData.id)}
      />
    </>
  )
}

const ButtonWrapper = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
`

const TableWrapper = styled.div`
  display: flex;
  gap: 0 20px;
  flex-wrap: wrap;
`

const BackArrowButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
`

const StatusesFilterStyled = styled(StatusesFilter)`
  > div {
    width: 100%;
    max-width: 280px;
  }
`
